import { SelectorAction } from '../../redux/selector/model'
import selectorActionCreatorsFor from '../../redux/selector/actionCreatorsFor'
import { BasicTarget, TARGET_PREFIX, TARGET_RESOURCE_NAME } from './model'
import { Dispatch, ExtraArgs, GetState } from '../../../types'
import { fetchLoaderHelper } from '../../redux/loader/thunksFor'
import { ARMApplicationDetail, ARMHybridDetail, RTFApplication } from '../../../remote-api/models'
import {
    getAppName,
    isARMApplicationDetail,
    isARMHybridDetail,
    isRTFApplication,
    Resource
} from '../model';
import * as moment from 'moment';

export type ResourceTargetAction =
    | SelectorAction<BasicTarget>

// Basic Actions Creators

export const resourceTargetActionCreators = (origin?: string) => {
    return {
        ...selectorActionCreatorsFor<BasicTarget>(TARGET_RESOURCE_NAME, TARGET_PREFIX, origin)
    }
}

// Thunks

export const fetchCHTargets = (resource: ARMApplicationDetail, origin?: string) => (dispatch: Dispatch) => {
    return fetchLoaderHelper<BasicTarget[]>(dispatch, resourceTargetActionCreators(origin), async () => {
        const workersLength = resource.workers.amount
        const workers = []

        for (let i = 0; i < workersLength; i++) {
            workers.push({id: `${i}`, name: `Worker ${i}`})
        }

        return Promise.resolve(workers)
    })
}

export const fetchHybridTargets = (resource: ARMHybridDetail, origin?: string) => (dispatch: Dispatch) => {
    return fetchLoaderHelper<BasicTarget[]>(dispatch, resourceTargetActionCreators(origin), async () => {
        const target = resource.target

        if (target.servers) {
            // includes cluster_id/server_group_id + individual servers
            const servers = target.servers.map(s => ({id: `${s.id}`, name: s.name}))
            const targets = [{id: `${target.id}`, name: target.name}, ...servers]
            return Promise.resolve(targets)
        } else {
            return Promise.resolve([{id: `${target.id}`, name: target.name}])
        }
    })
}

export const fetchRTFTargets = (resource: RTFApplication, origin?: string) => (dispatch: Dispatch) => {
    return fetchLoaderHelper<BasicTarget[]>(dispatch, resourceTargetActionCreators(origin), async () => {
        if (resource.replicas) {
            return Promise.resolve(resource.replicas.filter(r => r.id).map(r => ({id: `${r.id}`, name: r.id})))
        } else {
            return Promise.resolve([{id: `${resource.id}`, name: resource.name}])
        }
    })
}

export const fetchResourceTargets = (resource: Resource, origin?: string) => (dispatch: Dispatch) => {
    if (isARMApplicationDetail(resource)) return dispatch(fetchCHTargets(resource, origin))
    if (isARMHybridDetail(resource)) return dispatch(fetchHybridTargets(resource, origin))
    if (isRTFApplication(resource)) return dispatch(fetchRTFTargets(resource, origin))

    return fetchLoaderHelper<BasicTarget[]>(dispatch, resourceTargetActionCreators(origin), async () => {
        return Promise.resolve([])
    })
}

export const resolveTargetNames = (resource: Resource, appName: string, target: string)  => {
    if (isARMApplicationDetail(resource)) {
        const chTargetId = target.split('-')[1];
        return ({id: `${appName}_${chTargetId}`, name: target})
    }
    if (isARMHybridDetail(resource)) {
        const resourceTarget = resource.target
        if (resourceTarget) {
            const servers = resourceTarget.servers && resourceTarget.servers.map(s => ({id: `${s.id}`, name: s.name})) || []
            const currentTargets = [{id: `${resourceTarget.id}`, name: resourceTarget.name}, ...servers]
            const index = currentTargets.findIndex(x => x.id === target)
            const targetName = index >= 0 ? currentTargets[index].name : target
            return ({id: `${appName}_${target}`, name: targetName})
        }
    }
    return ({id: `${appName}_${target}`, name: target})
}
export const fetchAllRawDataTargets = (resource: Resource, env: string) =>
    (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
        const orgId = getState().monitoring.auth.profile.organizationId
        const archiveAPI = api.archiveAPI(getState)
        const appName = getAppName(resource)
        const selectedDate = moment.utc(getState().monitoring.rawData.selectedDate)
        const dateDiff = moment().utc().diff(selectedDate, 'days')

        // 365 days is the retention policy of influx
        if (dateDiff < 365) {
          const dateFormat = 'YYYY-MM-DD HH:mm:ss'
          return api.ui(getState).fetchTargetsFromInflux(orgId, env, appName, selectedDate.format(dateFormat), selectedDate.add( 1, 'days').format(dateFormat), isARMHybridDetail(resource))
              .then(data => {
                  return Promise.resolve(data.targets.map( (target: string) => {
                      return resolveTargetNames(resource, appName, target)
                  }))
              })
        }

        return archiveAPI.fetchS3FolderNames(orgId, env, resource).then (
          folderNames => {
             return Promise.resolve(folderNames.resources.filter(folderName => folderName.id.split('_').slice(-2)[0]
                 .includes(appName))
                 .map(r => ({id: `${r.id}`, name: r.id.split('_').slice(-1).toString()})))
          })
}

export const setTargets = (targets: BasicTarget[], origin?: string) => (dispatch: Dispatch) => {
    return fetchLoaderHelper<BasicTarget[]>(dispatch, resourceTargetActionCreators(origin), async () => {
        return Promise.resolve(targets)
    })
}

export const selectResourceTargets = (targets?: BasicTarget[], origin?: string) => (dispatch: Dispatch) => {
    dispatch(resourceTargetActionCreators(origin).resourcesSelected(targets))
}
